/**
 * @file inward-received-goods.tsx
 * @module app/modules/inward/components/inward-received-goods
 * @description "Received goods" table of an Inward GRN: per SKU and unit the selling price,
 *   new (Price Catalog) selling price, GP %, PO qty, received qty and unit, remaining on the
 *   PO, missing, damaged, tax %, tax amount and total amount, with each product's Finance /
 *   stock status. Finance (whoever has Finance Approval) ticks products (one or all) and enters
 *   the invoice cost price; a cost different from the PO updates the Price Catalog cost and
 *   raises a Tier 1 alert once approved.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { Form, Table } from "react-bootstrap";
import Method from "../../../../utils/methods";
import {
  COST_CHANGE_PENDING,
  ReceivedLine,
  ReceivedTotals,
} from "../utils/inward-approval";

interface InwardReceivedGoodsProps {
  lines: ReceivedLine[];
  totals?: ReceivedTotals;
  /** Finance selection mode: tick boxes + cost inputs on products still open */
  selectable?: boolean;
  selected?: Record<string, string>;
  onToggle?: (line: ReceivedLine, checked: boolean) => void;
  onToggleAll?: (checked: boolean) => void;
  onCostChange?: (line: ReceivedLine, value: string) => void;
}

const money = (n: number | null | undefined) =>
  n === null || n === undefined ? "-" : `TSh ${Method.getGeneralizedAmount(n)}`;

/** Finance / stock state of one product. */
const lineStatus = (l: ReceivedLine) => {
  if (l.received <= 0) return { label: "Nothing received", tone: "neutral" };
  if (l.stockLive) return { label: "Live in stock", tone: "success" };
  if (l.priceBlocked) {
    return {
      label: "Selling price below cost · procurement to update",
      tone: "danger",
    };
  }
  if (l.financeApproved) {
    return { label: "Finance approved · waits for Warehouse", tone: "warning" };
  }
  return { label: "Finance pending", tone: "danger" };
};

/** The cost Finance will approve (typed, else the shown cost) is above the selling price. */
export const belowSelling = (
  l: ReceivedLine,
  selected: Record<string, string>
) => {
  if (l.newSellingPrice === null || l.newSellingPrice === undefined)
    return false;
  const typed = selected[l.key];
  const cost =
    typed !== undefined && typed.trim() !== "" ? Number(typed) : l.costPrice;
  return Number.isFinite(cost) && cost > l.newSellingPrice;
};

/** Finance typed a cost different from the PO cost for this line. */
export const costChanged = (
  l: ReceivedLine,
  selected: Record<string, string>
) => {
  const typed = selected[l.key];
  if (typed === undefined || typed.trim() === "") return false;
  const n = Number(typed);
  return Number.isFinite(n) && n !== l.poCostPrice;
};

const InwardReceivedGoods = ({
  lines,
  totals,
  selectable = false,
  selected = {},
  onToggle,
  onToggleAll,
  onCostChange,
}: InwardReceivedGoodsProps) => {
  const open = lines.filter((l) => l.received > 0 && !l.financeApproved);
  const allSelected = open.length > 0 && open.every((l) => l.key in selected);

  return (
    <div className="table-responsive inward-received">
      <Table
        bordered
        className="align-middle fs-14 mb-0"
        data-testid="grn-received-goods"
      >
        <thead>
          <tr>
            {selectable && (
              <th>
                <Form.Check
                  aria-label="Select all open products"
                  checked={allSelected}
                  disabled={open.length === 0}
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                  data-testid="grn-select-all"
                />
              </th>
            )}
            <th>Product</th>
            <th className="text-end">Selling price</th>
            <th className="text-end">New selling price</th>
            <th className="text-end">GP %</th>
            <th className="text-end">PO qty</th>
            <th>Qty & type</th>
            <th className="text-end">Remaining</th>
            <th className="text-end">Missing</th>
            <th className="text-end">Damaged</th>
            <th className="text-end">PO cost price</th>
            <th className="text-end inward-cost-col">
              Cost price
              {selectable && (
                <div className="fs-12 fw-normal">entered by Finance</div>
              )}
            </th>
            <th className="text-end">Tax %</th>
            <th className="text-end">Total tax</th>
            <th className="text-end">Total amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const status = lineStatus(l);
            const canPick = selectable && l.received > 0 && !l.financeApproved;
            const isPicked = l.key in selected;
            return (
              <tr key={l.key} data-testid={`grn-line-${l.key}`}>
                {selectable && (
                  <td>
                    {canPick && (
                      <Form.Check
                        aria-label={`Select ${l.title}`}
                        checked={isPicked}
                        onChange={(e) => onToggle?.(l, e.target.checked)}
                        data-testid="grn-line-select"
                      />
                    )}
                  </td>
                )}
                <td className="fw-semibold">{l.title}</td>
                <td className="text-end">{money(l.sellingPrice)}</td>
                <td className="text-end" data-testid="grn-line-new-price">
                  {l.received > 0 && l.newSellingPrice === null ? (
                    <span className="text-danger fw-semibold">Missing</span>
                  ) : (
                    money(l.newSellingPrice)
                  )}
                </td>
                <td className="text-end">
                  {l.gpPercent === null ? "-" : `${l.gpPercent}%`}
                </td>
                <td className="text-end">{l.poQty}</td>
                <td data-testid="grn-line-qty">
                  <strong>{l.received}</strong> {l.unit}
                </td>
                <td
                  className="text-end fw-semibold"
                  data-testid="grn-line-remaining"
                >
                  {l.remaining}
                </td>
                <td className="text-end">{l.missing}</td>
                <td className="text-end">{l.damaged}</td>
                <td className="text-end" data-testid="grn-line-po-cost">
                  {money(l.poCostPrice)}
                </td>
                <td className="text-end inward-cost-col">
                  {canPick ? (
                    <>
                      <Form.Control
                        size="sm"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        className={`inward-cost-input ${
                          costChanged(l, selected)
                            ? "inward-cost-input--changed"
                            : ""
                        }`}
                        value={
                          isPicked ? selected[l.key] : String(l.poCostPrice)
                        }
                        onChange={(e) => onCostChange?.(l, e.target.value)}
                        aria-label={`Invoice cost price for ${l.title}`}
                        data-testid="grn-line-cost"
                      />
                      {costChanged(l, selected) && (
                        <div
                          className="fs-12 fw-semibold text-danger mt-1"
                          data-testid="grn-line-cost-hint"
                        >
                          Differs from PO: updates Price Catalog cost + Tier 1
                          alert
                        </div>
                      )}
                      {belowSelling(l, selected) && (
                        <div
                          className="fs-12 fw-semibold text-danger mt-1"
                          data-testid="grn-line-negative-gp"
                        >
                          Above the selling price ({l.newSellingPrice}): stays
                          out of stock until procurement updates the selling
                          price
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {money(l.costPrice)}
                      {l.costChange && (
                        <div
                          className={`fs-12 fw-semibold ${
                            l.costChange.status === COST_CHANGE_PENDING
                              ? "text-danger"
                              : "text-success"
                          }`}
                          data-testid="grn-line-cost-change"
                        >
                          PO {money(l.costChange.poCost)} ·{" "}
                          {l.costChange.status === COST_CHANGE_PENDING
                            ? "Tier 1 alert, waiting for acknowledgement"
                            : "acknowledged"}
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="text-end">{l.taxPercent}%</td>
                <td className="text-end">{money(l.taxAmount)}</td>
                <td className="text-end fw-semibold">{money(l.totalAmount)}</td>
                <td>
                  <span
                    className={`inward-badge inward-badge--${status.tone} fs-12`}
                    data-testid="grn-line-status"
                  >
                    {status.label}
                  </span>
                  {l.financeBy && (
                    <div className="fs-12 text-muted mt-1">
                      by {l.financeBy}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        {totals && (
          <tfoot>
            <tr className="fw-bold" data-testid="grn-received-totals">
              {selectable && <td />}
              <td colSpan={5}>
                Total · {totals.skuCount} SKUs · {totals.liveLines} of{" "}
                {totals.lineCount} products live
              </td>
              <td>{totals.received}</td>
              <td className="text-end">{totals.remaining}</td>
              <td className="text-end">{totals.missing}</td>
              <td className="text-end">{totals.damaged}</td>
              <td />
              <td className="text-end">{money(totals.amount)}</td>
              <td />
              <td className="text-end">{money(totals.taxAmount)}</td>
              <td className="text-end">{money(totals.totalAmount)}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </Table>
    </div>
  );
};

export default InwardReceivedGoods;
